import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from 'config/axiosBaseQuery';
import { endpoints } from 'config/api';
import {
  AccountingOverview,
  Invoice,
  ProjectInvoiceStats,
  ProjectProfitability,
  CompanyInvoiceStats,
  CreateInvoiceDto,
  UpdateInvoiceDto,
  RecordPaymentDto,
  SendInvoiceDto,
  OverviewQueryParams,
  CompanyInvoicesQueryParams,
  CompanyStatsQueryParams,
} from './accountingTypes';

export const accountingApi = createApi({
  reducerPath: 'accountingApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['AccountingOverview', 'Invoices', 'Invoice', 'InvoiceStats', 'ProjectProfitability'],
  endpoints: (builder) => ({
    // ==================
    // Accounting Overview
    // ==================

    getAccountingOverview: builder.query<AccountingOverview, OverviewQueryParams>({
      query: ({ month, year }) => ({
        url: endpoints.accounting.overview,
        method: 'GET',
        params: { month, year },
      }),
      transformResponse: (response: { success: boolean; data: AccountingOverview }) => response.data,
      providesTags: ['AccountingOverview'],
    }),

    // ==================
    // Project Invoices
    // ==================

    getProjectInvoices: builder.query<Invoice[], string>({
      query: (projectId) => ({
        url: endpoints.invoices.byProject(projectId),
        method: 'GET',
      }),
      transformResponse: (response: { success: boolean; data: Invoice[] }) => response.data,
      providesTags: (_res, _err, projectId) => [
        { type: 'Invoices', id: projectId },
        'Invoices',
      ],
    }),

    getProjectInvoiceStats: builder.query<ProjectInvoiceStats, string>({
      query: (projectId) => ({
        url: endpoints.invoices.projectStats(projectId),
        method: 'GET',
      }),
      transformResponse: (response: { success: boolean; data: ProjectInvoiceStats }) => response.data,
      providesTags: (_res, _err, projectId) => [
        { type: 'InvoiceStats', id: projectId },
      ],
    }),

    getBulkProjectInvoiceStats: builder.query<Record<string, ProjectInvoiceStats>, string[]>({
      query: (projectIds) => ({
        url: endpoints.invoices.bulkProjectStats,
        method: 'GET',
        params: { projectIds: projectIds.join(',') },
      }),
      transformResponse: (response: { success: boolean; data: Record<string, ProjectInvoiceStats> }) =>
        response.data,
      providesTags: (result) => {
        if (!result) return ['InvoiceStats'];
        return [
          ...Object.keys(result).map((projectId) => ({
            type: 'InvoiceStats' as const,
            id: projectId,
          })),
          'InvoiceStats',
        ];
      },
    }),

    getProjectProfitability: builder.query<ProjectProfitability, string>({
      query: (projectId) => ({
        url: endpoints.accounting.projectProfitability(projectId),
        method: 'GET',
      }),
      transformResponse: (response: { success: boolean; data: ProjectProfitability }) => response.data,
      providesTags: (_res, _err, projectId) => [
        { type: 'ProjectProfitability', id: projectId },
      ],
    }),

    createInvoice: builder.mutation<Invoice, { projectId: string; data: CreateInvoiceDto }>({
      query: ({ projectId, data }) => ({
        url: endpoints.invoices.byProject(projectId),
        method: 'POST',
        data,
      }),
      transformResponse: (response: { success: boolean; data: Invoice }) => response.data,
      invalidatesTags: (_res, _err, { projectId }) => [
        { type: 'Invoices', id: projectId },
        'Invoices',
        'AccountingOverview',
        'InvoiceStats',
      ],
    }),

    // ==================
    // Work Order Invoices
    // ==================

    getWorkOrderInvoices: builder.query<Invoice[], string>({
      query: (workOrderId) => ({
        url: endpoints.invoices.byWorkOrder(workOrderId),
        method: 'GET',
      }),
      transformResponse: (response: { success: boolean; data: Invoice[] }) => response.data,
      providesTags: (_res, _err, workOrderId) => [
        { type: 'Invoices', id: `wo-${workOrderId}` },
        'Invoices',
      ],
    }),

    getWorkOrderInvoiceStats: builder.query<ProjectInvoiceStats, string>({
      query: (workOrderId) => ({
        url: endpoints.invoices.workOrderStats(workOrderId),
        method: 'GET',
      }),
      transformResponse: (response: { success: boolean; data: ProjectInvoiceStats }) => response.data,
      providesTags: (_res, _err, workOrderId) => [
        { type: 'InvoiceStats', id: `wo-${workOrderId}` },
      ],
    }),

    createWorkOrderInvoice: builder.mutation<Invoice, { workOrderId: string; data: CreateInvoiceDto }>({
      query: ({ workOrderId, data }) => ({
        url: endpoints.invoices.byWorkOrder(workOrderId),
        method: 'POST',
        data,
      }),
      transformResponse: (response: { success: boolean; data: Invoice }) => response.data,
      invalidatesTags: (_res, _err, { workOrderId }) => [
        { type: 'Invoices', id: `wo-${workOrderId}` },
        'Invoices',
        'AccountingOverview',
        'InvoiceStats',
      ],
    }),

    // ==================
    // Company Invoices
    // ==================

    getCompanyInvoices: builder.query<Invoice[], CompanyInvoicesQueryParams | void>({
      query: (params) => ({
        url: endpoints.invoices.company,
        method: 'GET',
        params: params ? {
          status: params.status,
          projectId: params.projectId,
        } : undefined,
      }),
      transformResponse: (response: { success: boolean; data: Invoice[] }) => response.data,
      providesTags: ['Invoices'],
    }),

    getCompanyInvoiceStats: builder.query<CompanyInvoiceStats, CompanyStatsQueryParams>({
      query: ({ month, year }) => ({
        url: endpoints.invoices.companyStats,
        method: 'GET',
        params: { month, year },
      }),
      transformResponse: (response: { success: boolean; data: CompanyInvoiceStats }) => response.data,
      providesTags: ['InvoiceStats'],
    }),

    // ==================
    // Single Invoice Operations
    // ==================

    getInvoice: builder.query<Invoice, string>({
      query: (id) => ({
        url: endpoints.invoices.byId(id),
        method: 'GET',
      }),
      transformResponse: (response: { success: boolean; data: Invoice }) => response.data,
      providesTags: (_res, _err, id) => [{ type: 'Invoice', id }],
    }),

    updateInvoice: builder.mutation<Invoice, { id: string; data: UpdateInvoiceDto }>({
      query: ({ id, data }) => ({
        url: endpoints.invoices.byId(id),
        method: 'PUT',
        data,
      }),
      transformResponse: (response: { success: boolean; data: Invoice }) => response.data,
      invalidatesTags: (_res, _err, { id }) => [
        { type: 'Invoice', id },
        'Invoices',
        'AccountingOverview',
        'InvoiceStats',
      ],
    }),

    deleteInvoice: builder.mutation<{ message: string }, string>({
      query: (id) => ({
        url: endpoints.invoices.byId(id),
        method: 'DELETE',
      }),
      invalidatesTags: ['Invoices', 'AccountingOverview', 'InvoiceStats'],
    }),

    sendInvoice: builder.mutation<Invoice, { id: string; data: SendInvoiceDto }>({
      query: ({ id, data }) => ({
        url: endpoints.invoices.send(id),
        method: 'POST',
        data,
      }),
      transformResponse: (response: { success: boolean; data: Invoice }) => response.data,
      invalidatesTags: (_res, _err, { id }) => [
        { type: 'Invoice', id },
        'Invoices',
        'AccountingOverview',
      ],
    }),

    recordPayment: builder.mutation<Invoice, { id: string; data: RecordPaymentDto }>({
      query: ({ id, data }) => ({
        url: endpoints.invoices.payments(id),
        method: 'POST',
        data,
      }),
      transformResponse: (response: { success: boolean; data: Invoice }) => response.data,
      invalidatesTags: (_res, _err, { id }) => [
        { type: 'Invoice', id },
        'Invoices',
        'AccountingOverview',
        'InvoiceStats',
      ],
    }),
  }),
});

export const {
  // Accounting Overview
  useGetAccountingOverviewQuery,

  // Project Invoices
  useGetProjectInvoicesQuery,
  useGetProjectInvoiceStatsQuery,
  useGetBulkProjectInvoiceStatsQuery,
  useGetProjectProfitabilityQuery,
  useCreateInvoiceMutation,

  // Work Order Invoices
  useGetWorkOrderInvoicesQuery,
  useGetWorkOrderInvoiceStatsQuery,
  useCreateWorkOrderInvoiceMutation,

  // Company Invoices
  useGetCompanyInvoicesQuery,
  useGetCompanyInvoiceStatsQuery,

  // Single Invoice Operations
  useGetInvoiceQuery,
  useUpdateInvoiceMutation,
  useDeleteInvoiceMutation,
  useSendInvoiceMutation,
  useRecordPaymentMutation,
} = accountingApi;
